/**
 * FeatureFlagContext - Runtime feature flags and site mode
 *
 * v3.94 - F-01: Runtime feature flags context - 2026-03-20
 *
 * Fetches /api/feature-flags once on mount.
 * Defaults to most restrictive state (waitlist) until flags resolve.
 * Never renders speculatively — no flash of open registration.
 *
 * siteMode values:
 *   "waitlist" — registration closed, show waitlist CTA only
 *   "pilot"    — invite-only registration, show "Request Access" + invite code secondary in nav
 *   "open"     — full open registration, show "Get Started" CTA
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { portalRequestUngated } from '../services/portalRequest'; // v4.28 - G10-B: one request wrapper, carries the chosen company

export type SiteMode = 'waitlist' | 'pilot' | 'open';

export interface FeatureFlags {
  siteMode: SiteMode;
  features: {
    pricing: boolean;  // controls pricing section + nav link
    about: boolean;    // controls about nav link (route always exists)
  };
  flagsLoaded: boolean; // false until first fetch resolves — render conservative defaults
}

// Most restrictive defaults — used until flags resolve AND on any error
const FAIL_CLOSED: FeatureFlags = {
  siteMode: 'waitlist',
  features: { pricing: false, about: false },
  flagsLoaded: false,
};

const FeatureFlagContext = createContext<FeatureFlags>(FAIL_CLOSED);

export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlags>(FAIL_CLOSED);

  useEffect(() => {
    const controller = new AbortController();
    // 3s timeout — if BFF is cold-starting, fail closed and render immediately
    const timeout = setTimeout(() => controller.abort(), 3000);

    portalRequestUngated('/api/feature-flags', { signal: controller.signal })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        const validModes: SiteMode[] = ['waitlist', 'pilot', 'open'];
        const rawMode = data.siteMode as string;
        const siteMode: SiteMode = validModes.includes(rawMode as SiteMode)
          ? (rawMode as SiteMode)
          : 'waitlist';

        setFlags({
          siteMode,
          features: {
            pricing: Boolean(data.features?.pricing),
            about: Boolean(data.features?.about),
          },
          flagsLoaded: true,
        });
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.error('feature-flags: fetch failed, using fail-closed defaults:', err.message);
        }
        // On timeout or any error: fail closed, mark loaded so app renders in locked-down state
        setFlags({ ...FAIL_CLOSED, flagsLoaded: true });
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, []);

  return (
    <FeatureFlagContext.Provider value={flags}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlags => useContext(FeatureFlagContext);

/**
 * Returns primary CTA config derived from siteMode.
 * Single source of truth — used by nav, hero, and final CTA block.
 */
// v3.95 - Fix: /waitlist route does not exist; CTA must navigate to /register (routes to WaitlistPage in pilot/waitlist mode) - 2026-03-20
export function getPrimaryCTA(siteMode: SiteMode): { text: string; href: string } {
  if (siteMode === 'open')  return { text: 'Get Started Free',    href: '/register' };
  if (siteMode === 'pilot') return { text: 'Request Early Access', href: '/register' };
  return                           { text: 'Join the Waitlist',    href: '/register' };
}
